'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConservationZone {
  id: number;
  name: string;
  country: string;
  lat: number;
  lng: number;
  rhinoCount: number;
  status: string;
  rangers: number;
  area: string;
}

const ConservationMap = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedZone, setSelectedZone] = useState<ConservationZone | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const zones: ConservationZone[] = [
    {
      id: 1,
      name: 'Kruger National Park',
      country: 'South Africa',
      lat: -24.0094,
      lng: 31.4987,
      rhinoCount: 2750,
      status: 'Recovering But Historically Declined',
      rangers: 600,
      area: '19,485 km²'
    },
    {
      id: 2,
      name: 'Etosha National Park',
      country: 'Namibia',
      lat: -18.8556,
      lng: 16.3293,
      rhinoCount: 1200,
      status: 'Improving',
      rangers: 280,
      area: '22,270 km²'
    },
    {
      id: 3,
      name: 'Ol Pejeta Conservancy',
      country: 'Kenya',
      lat: 0.0236,
      lng: 36.8987,
      rhinoCount: 156,
      status: 'Critical',
      rangers: 145,
      area: '360 km²'
    },
    {
      id: 4,
      name: 'Hluhluwe-iMfolozi Park',
      country: 'South Africa',
      lat: -28.2667,
      lng: 31.9000,
      rhinoCount: 1500,
      status: 'Stable',
      rangers: 325,
      area: '960 km²'
    },
    {
      id: 5,
      name: 'Lewa Wildlife Conservancy',
      country: 'Kenya',
      lat: 0.2167,
      lng: 37.4167,
      rhinoCount: 198,
      status: 'Improving',
      rangers: 165,
      area: '250 km²'
    },
    {
      id: 6,
      name: 'Matobo National Park',
      country: 'Zimbabwe',
      lat: -20.5500,
      lng: 28.5000,
      rhinoCount: 125,
      status: 'Stable(Fragile)',
      rangers: 100,
      area: '3,100 km²'
    }
  ];

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === 'critical') return 'status-critical';
    if (s === 'stable' || s === 'stable/recovering') return 'status-stable';
    if (s.includes('fragile')) return 'status-stable-fragile';
    if (s === 'improving') return 'status-improving';
    if (s.includes('recovering') || s.includes('historically')) return 'status-recovering';
    return 'bg-muted text-muted-foreground';
  };

  const getStatusIcon = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === 'critical') return 'ExclamationTriangleIcon';
    if (s === 'stable' || s === 'stable/recovering' || s.includes('fragile')) return 'MinusIcon';
    if (s === 'improving') return 'ArrowTrendingUpIcon';
    if (s.includes('recovering') || s.includes('historically')) return 'ArrowPathIcon';
    return 'QuestionMarkCircleIcon';
  };

  const getStatusLabel = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === 'critical') return 'Critical';
    if (s === 'stable') return 'Stable';
    if (s === 'stable/recovering') return 'Stable / Recovering';
    if (s.includes('fragile')) return 'Stable (Fragile)';
    if (s === 'improving') return 'Improving';
    if (s.includes('recovering') || s.includes('historically')) return 'Recovering But Historically Declined';
    return status;
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
              Active Conservation Zones
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
              Explore our network of protected areas across Africa where dedicated teams work around the clock to safeguard rhino populations.
            </p>
          </div>
          <div className="bg-card rounded-lg shadow-organic p-8 border border-border">
            <div className="w-full h-96 bg-muted animate-pulse rounded" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Active Conservation Zones
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Explore our network of protected areas across Africa where dedicated teams work around the clock to safeguard rhino populations.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg shadow-organic p-4 border border-border h-[600px]">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="African Conservation Zones Map"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=-10.0,25.0&z=4&output=embed"
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-4 lg:max-h-[600px] lg:overflow-y-auto">
            {zones.map((zone) => (
              <button
                key={zone.id}
                onClick={() => setSelectedZone(zone)}
                className={`w-full text-left bg-card rounded-lg p-4 shadow-organic-sm border transition-organic duration-300 hover:shadow-organic ${
                  selectedZone?.id === zone.id ? 'border-primary' : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                      {zone.name}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body flex items-center">
                      <Icon name="MapPinIcon" size={14} variant="solid" className="mr-1" />
                      {zone.country}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${getStatusColor(zone.status)}`}>
                    <Icon name={getStatusIcon(zone.status) as any} size={14} variant="solid" className="inline mr-1" />
                    {getStatusLabel(zone.status)}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground font-body mb-1">Rhinos</p>
                    <p className="text-base font-headline font-bold text-foreground">{zone.rhinoCount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-body mb-1">Rangers</p>
                    <p className="text-base font-headline font-bold text-foreground">{zone.rangers}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-body mb-1">Area</p>
                    <p className="text-base font-headline font-bold text-foreground">{zone.area}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {selectedZone && (
          <div className="mt-8 bg-primary/5 rounded-lg p-6 border border-primary/20">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
                  {selectedZone.name}
                </h3>
                <p className="text-base text-muted-foreground font-body">
                  {selectedZone.country} • {selectedZone.area}
                </p>
              </div>
              <button
                onClick={() => setSelectedZone(null)}
                className="p-2 hover:bg-muted rounded-lg transition-organic duration-300"
              >
                <Icon name="XMarkIcon" size={20} variant="outline" />
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-card rounded-lg p-4 shadow-organic-sm">
                <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-primary mb-3" />
                <p className="text-2xl font-headline font-bold text-foreground mb-1">
                  {selectedZone.rhinoCount.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground font-body">Protected Rhinos</p>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-organic-sm">
                <Icon name="UserGroupIcon" size={32} variant="solid" className="text-secondary mb-3" />
                <p className="text-2xl font-headline font-bold text-foreground mb-1">
                  {selectedZone.rangers}
                </p>
                <p className="text-sm text-muted-foreground font-body">Active Rangers</p>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-organic-sm">
                <Icon name={getStatusIcon(selectedZone.status) as any} size={32} variant="solid" className="text-warning mb-3" />
                <p className="text-2xl font-headline font-bold text-foreground mb-1 capitalize">
                  {selectedZone.status}
                </p>
                <p className="text-sm text-muted-foreground font-body">Conservation Status</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ConservationMap;